<template>
  <div
    class="min-h-[stretch] tablet-h:min-h-screen max-h-[stretch] tablet-h:max-h-screen bg-[#FAFAFA] overflow-hidden"
  >
    <div
      class="flex flex-col px-3 py-5 tablet-v:px-4 tablet-h:px-5 w-full overflow-y-auto"
      :style="containerStyles"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <NewLink
          v-if="!loading"
          type="breadcrumb"
          class="mb-5"
          @click="router.push(fileRoute)"
        >
          Copyright
        </NewLink>

        <!-- Header skeleton -->
        <div
          v-if="loading"
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#CCC]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <Skeleton class="min-w-[80px] h-[80px] rounded-md" />
          <div class="flex-1">
            <Skeleton class="w-[200px] h-[26px] mb-3" />
            <div class="flex gap-x-10">
              <div>
                <Skeleton class="w-[120px] h-[18px] mb-1.5" />
                <Skeleton class="w-[160px] h-[18px]" />
              </div>
              <div>
                <Skeleton class="w-[100px] h-[18px] mb-1.5" />
                <Skeleton class="w-[140px] h-[18px]" />
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div
          v-else
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#CCC]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <div
            class="min-w-[80px] h-[80px] border border-[#CCC]/50 rounded-md bg-[#FAFAFA] flex items-center justify-center"
          >
            <ThumbnailIcon
              class="w-[46px] h-[46px]"
              :type="file?.type"
              :icon-size="46"
              :image-url="applyAuthorizationToken(file?.asset.previewImageUrl)"
              showShadow
              transition
            />
          </div>

          <div>
            <div class="flex mb-3 gap-x-5">
              <div
                class="font-primary font-medium text-[17px] leading-[26px] text-gray-800"
              >
                {{ file?.name }}
              </div>

              <RouterLink
                :to="fileRoute"
                class="w-fit! tablet-h:hidden self-start ml-auto"
              >
                <UiButton>View</UiButton>
              </RouterLink>
            </div>

            <div
              class="flex flex-col tablet-h:flex-row tablet-h:items-center gap-x-10 gap-y-4"
            >
              <div>
                <div
                  class="font-primary font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <Icon :size="16" name="Dacr" class="fill-gray-800 mr-1" />
                  <div>Dacr Timestamp</div>
                </div>

                <div
                  class="font-secondary text-[14px] leading-[18px] text-gray-800 flex items-center"
                >
                  {{
                    formatTimestamp(file?.copyright.registrations[0]?.timestamp)
                  }}
                </div>
              </div>

              <div>
                <div
                  class="font-primary font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <div>Dacr Token ID</div>
                </div>

                <div
                  class="font-secondary text-[14px] leading-[18px] text-gray-800 flex items-center break-all"
                >
                  {{ file?.copyright.id }}
                </div>
              </div>
            </div>
          </div>

          <RouterLink
            :to="fileRoute"
            class="hidden tablet-h:block self-start ml-auto"
          >
            <UiButton>View registration</UiButton>
          </RouterLink>
        </div>

        <Card.Base
          class="mb-4 border-2 border-transparent border--glow relative"
        >
          <Card.Title icon="Text">
            <div>Transcript</div>

            <div
              class="text-[12px] leading-[15px] bg-gradient-to-r from-[#30BAC6]/20 from-60% to-[#69B393]/20 absolute top-0 right-0 rounded-tr-[10px] rounded-bl-xl h-7 text-gray-900 flex items-center px-3"
            >
              <img :src="Fingerprint" class="w-[14px] mr-2" />
              Registered for protection
            </div>
          </Card.Title>

          <Card.Content>
            <div class="mb-[22px]">
              <div>
                Analyzed with
                <span
                  class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text font-medium text-transparent"
                >
                  Dacr AI
                </span>
              </div>
            </div>

            <!-- Content skeleton -->
            <template v-if="transcriptLoading">
              <div v-for="n in 5" :key="n" class="flex mb-1">
                <div class="min-w-[90px] mr-3">
                  <Skeleton class="w-[50px] h-[20px]" />
                </div>
                <Skeleton
                  class="h-[20px]"
                  :style="{ width: `${20 + ((n * 8) % 25)}%` }"
                />
              </div>
            </template>

            <!-- Content -->
            <template v-else>
              <div
                v-for="entry in transcript"
                :key="entry.timestamp"
                class="leading-[20px] flex"
              >
                <div class="min-w-[90px] text-gray-500 tabular-nums">
                  {{ entry.timestamp }}
                </div>
                <div>{{ entry.text }}</div>
              </div>
            </template>
          </Card.Content>
        </Card.Base>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NewLink from '@/shared/components/NewLink.vue'
import { useCopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { Card, Icon, Skeleton, ThumbnailIcon, UiButton } from '@dacr/ui'
import axios from 'axios'
import { computed, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Fingerprint from '@/assets/images/Fingerprint.png'

const { availableHeight } = useAvailableHeight()
const route = useRoute()
const router = useRouter()

const fileId = computed(() => route.params.id as string)
const trackIndex = computed(() =>
  route.params.trackIndex !== undefined ? Number(route.params.trackIndex) : null
)

const { file, loading } = useCopyrightFile(fileId)

const transcriptUrl = computed(() => {
  const analysis = file.value?.analysis
  if (!analysis) return null

  if (
    trackIndex.value !== null &&
    (analysis.__typename === 'VideoAnalysis' ||
      analysis.__typename === 'AudioAnalysis')
  ) {
    return analysis.tracks[trackIndex.value]?.transcript?.url ?? null
  }

  if (
    analysis.__typename === 'AudioAnalysis' ||
    analysis.__typename === 'VideoAnalysis'
  ) {
    return analysis.transcript.url
  }

  return null
})

const transcript = ref<{ timestamp: string; text: string }[]>([])
const transcriptLoading = ref(true)

watchEffect(async (onCleanup) => {
  const url = transcriptUrl.value
  if (!url) {
    transcript.value = []
    return
  }

  transcriptLoading.value = true

  let cancelled = false
  onCleanup(() => {
    cancelled = true
  })

  try {
    const { data } = await axios.get(url)
    if (cancelled) return
    transcript.value = data.lines
  } catch {
    transcript.value = []
  } finally {
    if (!cancelled) transcriptLoading.value = false
  }
})

const containerStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const fileRoute = computed(() => `/file/${route.params.id}`)
</script>
